import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, TrendingUp, Briefcase } from 'lucide-react';
import { useMarketPrices } from '@/hooks/useMarketPrices';
import { instrumentRegistry, Instrument } from '@/data/instrumentRegistry';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';

interface InstrumentListProps {
  activeSymbol: string;
  onSelect: (instrument: Instrument) => void;
  connectedBroker?: string | null;
}

// Default groups
const defaultGroups: { label: string; filter: (i: Instrument) => boolean }[] = [
  { label: 'MAJORS', filter: i => i.asset_class === 'forex' && i.category === 'Major' && i.supported },
  { label: 'CROSSES', filter: i => i.asset_class === 'forex' && i.category === 'Cross' && i.supported },
  { label: 'COMMODITIES', filter: i => i.asset_class === 'commodity' && i.supported },
  { label: 'CRYPTO', filter: i => i.asset_class === 'crypto' && i.supported },
  { label: 'STOCKS', filter: i => i.asset_class === 'stock' && i.supported },
  { label: 'INDICES', filter: i => i.asset_class === 'index' && i.supported },
];

// Broker-specific group configs
const indianBrokers = ['zerodha', 'dhan', 'upstox', 'angelone', 'angel one'];
const cryptoBrokers = ['binance', 'coinbase', 'kraken', 'bybit', 'kucoin'];
const forexBrokers = ['metatrader', 'oanda', 'xm'];
const usBrokers = ['alpaca', 'interactive brokers'];

function getBrokerGroups(broker: string | null | undefined): typeof defaultGroups {
  if (!broker) return defaultGroups;
  const b = broker.toLowerCase();

  if (indianBrokers.some(ib => b.includes(ib))) {
    return [
      { label: 'NSE STOCKS', filter: i => i.asset_class === 'stock' && i.supported },
      { label: 'F&O', filter: i => i.asset_class === 'index' && i.supported },
      { label: 'COMMODITIES (MCX)', filter: i => i.asset_class === 'commodity' && i.supported },
      { label: 'CURRENCY', filter: i => i.asset_class === 'forex' && i.supported },
    ];
  }

  if (cryptoBrokers.some(cb => b.includes(cb))) {
    return [
      { label: 'BTC PAIRS', filter: i => i.asset_class === 'crypto' && i.internal_symbol.includes('BTC') && i.supported },
      { label: 'ETH PAIRS', filter: i => i.asset_class === 'crypto' && i.internal_symbol.includes('ETH') && i.supported },
      { label: 'ALTCOINS', filter: i => i.asset_class === 'crypto' && !i.internal_symbol.includes('BTC') && !i.internal_symbol.includes('ETH') && i.supported },
      { label: 'FOREX', filter: i => i.asset_class === 'forex' && i.supported },
      { label: 'COMMODITIES', filter: i => i.asset_class === 'commodity' && i.supported },
    ];
  }

  if (forexBrokers.some(fb => b.includes(fb))) {
    return [
      { label: 'MAJORS', filter: i => i.asset_class === 'forex' && i.category === 'Major' && i.supported },
      { label: 'CROSSES', filter: i => i.asset_class === 'forex' && i.category === 'Cross' && i.supported },
      { label: 'COMMODITIES', filter: i => i.asset_class === 'commodity' && i.supported },
      { label: 'INDICES', filter: i => i.asset_class === 'index' && i.supported },
      { label: 'CRYPTO CFDs', filter: i => i.asset_class === 'crypto' && i.supported },
    ];
  }

  if (usBrokers.some(ub => b.includes(ub))) {
    return [
      { label: 'US STOCKS', filter: i => i.asset_class === 'stock' && i.supported },
      { label: 'ETFs', filter: i => i.asset_class === 'index' && i.supported },
      { label: 'CRYPTO', filter: i => i.asset_class === 'crypto' && i.supported },
    ];
  }

  return defaultGroups;
}

export default function InstrumentList({ activeSymbol, onSelect, connectedBroker }: InstrumentListProps) {
  const { user } = useAuth();
  const { prices } = useMarketPrices(5000);
  const [search, setSearch] = useState('');
  const [flashMap, setFlashMap] = useState<Map<string, 'up' | 'down'>>(new Map());
  const prevPricesRef = useRef<Map<string, number>>(new Map());

  // Fetch portfolio holdings for suggestions
  const { data: holdings } = useQuery({
    queryKey: ['instrument-list-holdings', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('portfolio_holdings')
        .select('symbol, asset_name, current_price, unrealized_pnl_pct')
        .eq('user_id', user.id)
        .gt('quantity', 0)
        .limit(10);
      return data || [];
    },
    enabled: !!user,
    staleTime: 60000,
  });

  // Flash on price change
  useEffect(() => {
    const newFlash = new Map<string, 'up' | 'down'>();
    prices.forEach(p => {
      const prev = prevPricesRef.current.get(p.symbol);
      if (prev !== undefined && prev !== p.price) {
        newFlash.set(p.symbol, p.price > prev ? 'up' : 'down');
      }
      prevPricesRef.current.set(p.symbol, p.price);
    });
    if (newFlash.size > 0) {
      setFlashMap(newFlash);
      const t = setTimeout(() => setFlashMap(new Map()), 300);
      return () => clearTimeout(t);
    }
  }, [prices]);

  const getPrice = (inst: Instrument) => {
    return prices.find(
      p =>
        p.symbol === inst.internal_symbol ||
        p.symbol === inst.display_name ||
        p.symbol === `${inst.internal_symbol.slice(0, 3)}/${inst.internal_symbol.slice(3)}`
    );
  };

  const getFlash = (inst: Instrument) => {
    return (
      flashMap.get(inst.internal_symbol) ||
      flashMap.get(inst.display_name) ||
      flashMap.get(`${inst.internal_symbol.slice(0, 3)}/${inst.internal_symbol.slice(3)}`)
    );
  };

  // Top movers for "Trending Now"
  const topMovers = useMemo(() => {
    return [...prices]
      .sort((a, b) => Math.abs(b.change_percent) - Math.abs(a.change_percent))
      .slice(0, 5);
  }, [prices]);

  // Portfolio holdings mapped to instruments
  const portfolioInstruments = useMemo(() => {
    if (!holdings || holdings.length === 0) return [];
    return holdings
      .map(h => {
        const inst = instrumentRegistry.find(
          i => i.internal_symbol === h.symbol || i.display_name === h.symbol
        );
        return inst ? { instrument: inst, holding: h } : null;
      })
      .filter(Boolean) as { instrument: Instrument; holding: typeof holdings[0] }[];
  }, [holdings]);

  const groups = useMemo(() => getBrokerGroups(connectedBroker), [connectedBroker]);

  const filteredGroups = useMemo(() => {
    const q = search.toLowerCase();
    return groups
      .map(g => ({
        label: g.label,
        items: instrumentRegistry.filter(g.filter).filter(
          i =>
            !q ||
            i.display_name.toLowerCase().includes(q) ||
            i.internal_symbol.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.items.length > 0);
  }, [search, groups]);

  const hasPortfolio = portfolioInstruments.length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ borderRight: '1px solid #1a1a2e' }}>
      {/* Search */}
      <div className="p-2 shrink-0" style={{ borderBottom: '1px solid #1a1a2e' }}>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search instruments..."
            className="w-full h-7 pl-7 pr-2 text-[11px] rounded bg-transparent text-gray-300 placeholder:text-gray-600 outline-none"
            style={{ border: '1px solid #1a1a2e' }}
          />
        </div>
      </div>

      {/* Instrument List */}
      <div className="flex-1 overflow-y-auto">
        {/* PORTFOLIO SUGGESTIONS or TRENDING NOW */}
        {!search && (
          hasPortfolio ? (
            <div>
              <div
                className="sticky top-0 px-3 py-1.5 text-[10px] font-bold tracking-widest flex items-center gap-1.5"
                style={{ background: '#0a0a0f', color: 'hsl(var(--accent-purple))' }}
              >
                <Briefcase className="w-3 h-3" />
                FROM YOUR PORTFOLIO
              </div>
              {portfolioInstruments.map(({ instrument: inst, holding }) => {
                const isActive = activeSymbol === inst.tradingview_symbol;
                const mp = getPrice(inst);
                return (
                  <button
                    key={`portfolio-${inst.internal_symbol}`}
                    onClick={() => onSelect(inst)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left transition-all duration-150 hover:bg-white/[0.03]"
                    style={{
                      background: isActive ? '#1a1a2e' : 'transparent',
                      borderLeft: isActive ? '2px solid hsl(var(--accent-purple))' : '2px solid transparent',
                    }}
                  >
                    <div>
                      <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>
                        {inst.display_name}
                      </span>
                      {holding.asset_name && (
                        <div className="text-[9px] text-gray-500 truncate max-w-[100px]">{holding.asset_name}</div>
                      )}
                    </div>
                    <div className="text-right">
                      {mp ? (
                        <>
                          <div className="text-[11px] font-mono text-gray-200">
                            {mp.price.toFixed(inst.decimals)}
                          </div>
                          <div
                            className={`text-[10px] font-mono ${
                              mp.change_percent >= 0 ? 'text-emerald-400' : 'text-red-400'
                            }`}
                          >
                            {mp.change_percent >= 0 ? '+' : ''}
                            {mp.change_percent.toFixed(2)}%
                          </div>
                        </>
                      ) : (
                        <div className="text-[10px] text-gray-600">—</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : topMovers.length > 0 ? (
            <div>
              <div
                className="sticky top-0 px-3 py-1.5 text-[10px] font-bold tracking-widest flex items-center gap-1.5"
                style={{ background: '#0a0a0f', color: 'hsl(var(--accent-amber))' }}
              >
                <TrendingUp className="w-3 h-3" />
                TRENDING NOW
              </div>
              {topMovers.map(mover => {
                const inst = instrumentRegistry.find(
                  i => i.internal_symbol === mover.symbol || i.display_name === mover.symbol
                );
                if (!inst) return null;
                const isActive = activeSymbol === inst.tradingview_symbol;
                return (
                  <button
                    key={`trending-${mover.symbol}`}
                    onClick={() => onSelect(inst)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left transition-all duration-150 hover:bg-white/[0.03]"
                    style={{
                      background: isActive ? '#1a1a2e' : 'transparent',
                      borderLeft: isActive ? '2px solid hsl(var(--accent-amber))' : '2px solid transparent',
                    }}
                  >
                    <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>
                      {inst.display_name}
                    </span>
                    <div className="text-right">
                      <div className="text-[11px] font-mono text-gray-200">
                        {mover.price.toFixed(inst.decimals)}
                      </div>
                      <div
                        className={`text-[10px] font-mono ${
                          mover.change_percent >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {mover.change_percent >= 0 ? '+' : ''}
                        {mover.change_percent.toFixed(2)}%
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null
        )}

        {/* Standard groups */}
        {filteredGroups.map(group => (
          <div key={group.label}>
            <div
              className="sticky top-0 px-3 py-1.5 text-[10px] font-bold tracking-widest text-gray-500"
              style={{ background: '#0a0a0f' }}
            >
              {group.label}
            </div>
            {group.items.map(inst => {
              const isActive = activeSymbol === inst.tradingview_symbol;
              const mp = getPrice(inst);
              const flash = getFlash(inst);

              return (
                <button
                  key={inst.internal_symbol}
                  onClick={() => onSelect(inst)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left transition-all duration-150 hover:bg-white/[0.03]"
                  style={{
                    background: isActive
                      ? '#1a1a2e'
                      : flash === 'up'
                      ? 'rgba(0,200,83,0.08)'
                      : flash === 'down'
                      ? 'rgba(255,23,68,0.08)'
                      : 'transparent',
                    borderLeft: isActive ? '2px solid #00c853' : '2px solid transparent',
                  }}
                >
                  <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>
                    {inst.display_name}
                  </span>
                  <div className="text-right">
                    {mp ? (
                      <>
                        <div className="text-[11px] font-mono text-gray-200">
                          {mp.price.toFixed(inst.decimals)}
                        </div>
                        <div
                          className={`text-[10px] font-mono ${
                            mp.change_percent >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {mp.change_percent >= 0 ? '+' : ''}
                          {mp.change_percent.toFixed(2)}%
                        </div>
                      </>
                    ) : (
                      <div className="text-[10px] text-gray-600">—</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
